import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ExternalLink, LogOut, ArrowLeft, Plus, Trash2, Link2,
  Globe, Send, Radio, RefreshCw, Check, X as XIcon,
} from "lucide-react";
import { useNoIndex } from "../lib/seoHelpers";
import { DIRECTORY_SEED } from "../data/directorySeed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  not_started: "bg-slate-500/20 text-slate-300",
  pending: "bg-yellow-500/20 text-yellow-300",
  submitted: "bg-blue-500/20 text-blue-300",
  live: "bg-green-500/20 text-green-300",
  rejected: "bg-red-500/20 text-red-300",
};

const KIND_TABS = [
  { key: "directory", label: "Directories", icon: Globe },
  { key: "outreach", label: "Outreach", icon: Send },
  { key: "haro", label: "HARO / Press", icon: Radio },
  { key: "referring_domain", label: "Referring Domains", icon: Link2 },
];

/**
 * /admin/backlinks — Backlink Command Center.
 * Tracks 4 kinds of link-building activity:
 *   - directory  → known-list submissions (100+ preloaded from seed)
 *   - outreach   → guest-post / partner-content pipeline
 *   - haro       → journalist requests + pitches
 *   - referring_domain → sites that already link to us (from GSC or auto-detected via embed widget)
 */
export default function AdminBacklinksPage() {
  useNoIndex();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeKind, setActiveKind] = useState("directory");
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("sdc_admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/backlinks`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/backlinks/stats`, { headers: getAuthHeaders() }),
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
      setError("");
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/admin");
        return;
      }
      setError("Failed to load backlinks. Try again.");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("sdc_admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    loadData();
  }, [loadData, navigate]);

  const filteredItems = useMemo(
    () => items.filter((i) => i.kind === activeKind),
    [items, activeKind]
  );

  const handleSeedDirectories = async () => {
    if (!window.confirm(`Seed ${DIRECTORY_SEED.length} directory targets? Existing rows are skipped.`)) return;
    setSeeding(true);
    try {
      const seedItems = DIRECTORY_SEED.map((d) => ({
        kind: "directory",
        name: d.name,
        url: d.url,
        category: d.category,
        tier: d.tier,
        status: "not_started",
      }));
      const res = await axios.post(
        `${API}/admin/backlinks/bulk-seed`,
        { items: seedItems },
        { headers: getAuthHeaders() }
      );
      alert(`Seeded ${res.data.inserted} new directory targets. Total: ${res.data.total}`);
      await loadData();
    } catch (e) {
      alert("Seed failed. See console for details.");
      console.error(e);
    } finally {
      setSeeding(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/backlinks/${id}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status: newStatus } : i)));
    } catch (e) {
      alert("Update failed.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    try {
      await axios.delete(`${API}/admin/backlinks/${id}`, { headers: getAuthHeaders() });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      alert("Delete failed.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sdc_admin_token");
    localStorage.removeItem("sdc_admin_user");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="admin-backlinks-page">
      {/* Header */}
      <header className="bg-[#252825] border-b border-[#3f4816]/50 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-[#888680] hover:text-[#d9fb06]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg md:text-xl font-black text-[#d9fb06] uppercase">
                Backlink Command Center
              </h1>
              <p className="text-xs text-[#888680] font-mono">Directory · Outreach · HARO · Referring domains</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadData}
              className="text-[#888680] hover:text-[#d9fb06] p-2"
              title="Refresh"
              data-testid="backlinks-refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={handleLogout}
              className="text-[#888680] hover:text-red-400 flex items-center gap-1.5 text-sm"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats strip */}
      {stats && (
        <section className="bg-[#302f2c] border-b border-[#3f4816]/40">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Tracked" value={stats.total} />
            <StatCard label="Live Backlinks" value={stats.live} highlight />
            <StatCard label="Submitted" value={stats.byStatus?.submitted || 0} />
            <StatCard label="Pending" value={stats.byStatus?.pending || 0} />
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className="bg-[#252825] border-b border-[#3f4816]/40">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-wrap gap-1">
          {KIND_TABS.map((tab) => {
            const Icon = tab.icon;
            const count = stats?.byKind?.[tab.key] || 0;
            const isActive = activeKind === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveKind(tab.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                  isActive
                    ? "border-[#d9fb06] text-[#d9fb06]"
                    : "border-transparent text-[#888680] hover:text-[#dfddd6]"
                }`}
                data-testid={`backlinks-tab-${tab.key}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {count > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#3f4816]/40 text-[#dfddd6]">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-[#dfddd6]">
            {filteredItems.length} row{filteredItems.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeKind === "directory" && (
            <button
              onClick={handleSeedDirectories}
              disabled={seeding}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#302f2c] border border-[#3f4816] text-[#d9fb06] text-sm hover:bg-[#3f4816]/40 transition disabled:opacity-50"
              data-testid="backlinks-seed-btn"
            >
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Seed {DIRECTORY_SEED.length} Directories
            </button>
          )}
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90 transition"
            data-testid="backlinks-add-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Row
          </button>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4">
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
            {error}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 pb-16">
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#d9fb06] mx-auto mb-2" />
            <p className="text-[#888680] text-sm">Loading…</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <EmptyState kind={activeKind} onSeed={handleSeedDirectories} />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[#3f4816]/40">
            <table className="w-full">
              <thead className="bg-[#302f2c] text-[#888680] text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-mono">Name</th>
                  <th className="text-left px-4 py-3 font-mono hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-3 font-mono">Status</th>
                  <th className="text-left px-4 py-3 font-mono hidden lg:table-cell">Live URL</th>
                  <th className="text-right px-4 py-3 font-mono">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm text-[#dfddd6]">
                {filteredItems.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t border-[#3f4816]/30 hover:bg-[#302f2c]/50 transition"
                    data-testid={`backlink-row-${item.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#dfddd6] hover:text-[#d9fb06] font-medium"
                        >
                          {item.name}
                        </a>
                        <ExternalLink className="w-3 h-3 text-[#888680] flex-shrink-0" />
                      </div>
                      <p className="text-xs text-[#888680] mt-0.5 truncate max-w-[300px]">{item.url}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-[#888680]">{item.category || "—"}</span>
                      {item.tier && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#d9fb06]/10 text-[#d9fb06]">
                          T{item.tier}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={item.status}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border-0 focus:ring-1 focus:ring-[#d9fb06] cursor-pointer ${STATUS_COLORS[item.status] || STATUS_COLORS.not_started}`}
                        data-testid={`backlink-status-${item.id}`}
                      >
                        <option value="not_started">Not Started</option>
                        <option value="pending">Pending</option>
                        <option value="submitted">Submitted</option>
                        <option value="live">Live</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {item.liveUrl ? (
                        <a
                          href={item.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#d9fb06] hover:underline text-xs"
                        >
                          View →
                        </a>
                      ) : (
                        <span className="text-[#888680] text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-[#888680] hover:text-red-400 p-1"
                        title="Delete"
                        data-testid={`backlink-delete-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <AddRowModal
          kind={activeKind}
          onClose={() => setShowAdd(false)}
          onCreated={(row) => {
            setItems((prev) => [row, ...prev]);
            setShowAdd(false);
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-[#d9fb06]/10 border border-[#d9fb06]/30" : "bg-[#1a1c1b] border border-[#3f4816]/40"}`}>
      <p className="text-xs uppercase tracking-widest text-[#888680] font-mono">{label}</p>
      <p className={`text-2xl font-black mt-1 ${highlight ? "text-[#d9fb06]" : "text-[#dfddd6]"}`}>{value}</p>
    </div>
  );
}

function EmptyState({ kind, onSeed }) {
  return (
    <div className="text-center py-16 rounded-xl border border-dashed border-[#3f4816]/50 bg-[#302f2c]/30">
      <p className="text-[#dfddd6] font-semibold mb-2">Nothing here yet.</p>
      <p className="text-sm text-[#888680] mb-4">
        {kind === "directory"
          ? "Seed the pre-loaded directory list or add rows manually."
          : "Add your first row to start tracking."}
      </p>
      {kind === "directory" && (
        <button
          onClick={onSeed}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90"
        >
          <Plus className="w-3.5 h-3.5" />
          Seed pre-loaded directories
        </button>
      )}
    </div>
  );
}

function AddRowModal({ kind, onClose, onCreated, getAuthHeaders }) {
  const [form, setForm] = useState({
    name: "",
    url: "",
    category: "",
    status: "not_started",
    anchorText: "",
    contactEmail: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/backlinks`,
        { kind, ...form },
        { headers: getAuthHeaders() }
      );
      onCreated(res.data);
    } catch (err) {
      alert("Failed to create.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="add-row-modal">
      <div className="bg-[#252825] border border-[#3f4816] rounded-2xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-[#d9fb06] uppercase">Add {kind.replace("_", " ")}</h3>
          <button onClick={onClose} className="text-[#888680] hover:text-[#dfddd6]">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="add-name" />
          <Field label="URL" required type="url" value={form.url} onChange={(v) => setForm({ ...form, url: v })} testid="add-url" />
          <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="add-category" />
          {(kind === "outreach" || kind === "haro") && (
            <>
              <Field label="Contact email" type="email" value={form.contactEmail} onChange={(v) => setForm({ ...form, contactEmail: v })} testid="add-contact" />
              <Field label="Anchor text (planned)" value={form.anchorText} onChange={(v) => setForm({ ...form, anchorText: v })} testid="add-anchor" />
            </>
          )}
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none"
              data-testid="add-notes"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-[#888680] hover:text-[#dfddd6]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="add-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#888680] mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#1a1c1b] border border-[#3f4816]/50 rounded-lg px-3 py-2 text-sm text-[#dfddd6] focus:border-[#d9fb06] outline-none"
        data-testid={testid}
      />
    </div>
  );
}
